import { useState, useEffect, useRef } from 'react';
import { 
  X, Phone, User, MapPin, Calendar, Clock, Save, Trash2, 
  FileText, MessageSquare, Bell, AlertTriangle, CheckCircle2,
  Edit3, ExternalLink, Loader2, Info, ArrowRight
} from 'lucide-react';
import getApiUrl from '../../utils/getApiUrl';

const API = `${getApiUrl()}/api`;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem("auth_token");
  return fetch(url, {
    ...options,
    credentials: "include",
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) },
  });
};


/**
 * Modal pour voir et éditer les détails d'un rappel
 */
const RappelDetailModal = ({ rappel, onClose, onUpdate, onDelete, onAppeler, onViewClientFiche }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const phoneRef = useRef(null);
  
  // État du formulaire d'édition
  const [formData, setFormData] = useState({
    date_rappel: rappel?.date_rappel || '',
    heure_rappel: rappel?.heure_rappel || '',
    notes: rappel?.notes || '',
    // Infos prospect
    nom: rappel?.nom || '',
    prenom: rappel?.prenom || '',
    telephone: rappel?.telephone || '',
    email: rappel?.email || '',
    adresse: rappel?.adresse || '',
    code_postal: rappel?.code_postal || '',
    ville: rappel?.ville || ''
  });

  useEffect(() => {
    if (rappel) {
      setFormData({
        date_rappel: rappel.date_rappel || '',
        heure_rappel: rappel.heure_rappel || '',
        notes: rappel.notes || '',
        nom: rappel.nom || '',
        prenom: rappel.prenom || '',
        telephone: rappel.telephone || '',
        email: rappel.email || '',
        adresse: rappel.adresse || '',
        code_postal: rappel.code_postal || '',
        ville: rappel.ville || ''
      });
      
      // Tenter de forcer Ringover à re-scanner le DOM
      setTimeout(() => {
        // Déclencher un événement de mutation pour que Ringover détecte le nouveau contenu
        window.dispatchEvent(new Event('resize'));
        // Aussi essayer de déclencher un événement personnalisé que Ringover pourrait écouter
        document.dispatchEvent(new CustomEvent('DOMContentLoaded'));
      }, 100);
    }
  }, [rappel]);

  if (!rappel) return null;

  // Formater le numéro pour affichage (avec espaces: 06 12 34 56 78)
  const formatPhoneDisplay = (phone) => {
    if (!phone) return '';
    let cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('33') && cleaned.length > 10) {
      cleaned = '0' + cleaned.substring(2);
    }
    if (cleaned.length === 9 && !cleaned.startsWith('0')) {
      cleaned = '0' + cleaned;
    }
    // Format avec espaces: 06 12 34 56 78
    if (cleaned.length === 10) {
      return cleaned.replace(/(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})/, '$1 $2 $3 $4 $5');
    }
    return cleaned;
  };

  // Formater le numéro pour href tel: (+33612345678)
  const formatPhoneTel = (phone) => {
    if (!phone) return '';
    let cleaned = phone.replace(/\D/g, '');
    if (cleaned.startsWith('0') && cleaned.length === 10) {
      return '+33' + cleaned.substring(1);
    }
    if (cleaned.startsWith('33')) {
      return '+' + cleaned;
    }
    return '+33' + cleaned;
  };

  // Vérifier si le rappel est en retard
  const isOverdue = () => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];
    if (rappel.date_rappel < today) return true;
    if (rappel.date_rappel === today && rappel.heure_rappel) {
      const [h, m] = rappel.heure_rappel.split(':');
      const rappelTime = new Date(now);
      rappelTime.setHours(parseInt(h), parseInt(m), 0, 0);
      return now > rappelTime;
    }
    return false;
  };

  // Formater la date pour l'affichage
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Non définie';
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    });
  };

  // Sauvegarder les modifications
  const handleSave = async () => {
    setSaving(true);
    try {
      // Mettre à jour le rappel
      const response = await authFetch(`${API}/webhook/leads/${rappel.lead_id}/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          date_rappel: formData.date_rappel,
          heure_rappel: formData.heure_rappel,
          notes: formData.notes,
          nom: formData.nom,
          prenom: formData.prenom,
          telephone: formData.telephone,
          email: formData.email,
          adresse: formData.adresse,
          code_postal: formData.code_postal,
          ville: formData.ville
        })
      });

      if (response.ok) {
        setIsEditing(false);
        if (onUpdate) onUpdate();
        onClose();
      } else {
        alert('❌ Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('❌ Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Supprimer le rappel (marquer comme traité)
  const handleDelete = async () => {
    if (!confirm('Supprimer ce rappel ?')) return;
    
    setDeleting(true);
    try {
      const response = await authFetch(`${API}/webhook/leads/${rappel.lead_id}/statut`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          statut: 'rappel_annule',
          etape_pipeline: 'lead_perdu_avant_r1',
          telepro_email: 'admin',
          telepro_nom: 'Admin',
          notes: 'Rappel annulé'
        })
      });

      if (response.ok) {
        if (onDelete) onDelete(rappel);
        onClose();
      }
    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setDeleting(false);
    }
  };

  // Appeler via Ringover SDK
  const handleClickToCall = () => {
    const phone = rappel.telephone;
    // Essayer d'abord le SDK Ringover
    if (window.ringoverDial && window.ringoverDial(phone)) {
      console.log("Appel via Ringover SDK");
    } else {
      // Fallback: ouvrir le lien tel:
      const phoneE164 = formatPhoneTel(phone);
      window.location.href = `tel:${phoneE164}`;
    }
  };

  const overdue = isOverdue();

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-[70] flex items-center justify-center p-4">
      <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className={`p-4 flex items-center justify-between ${
          overdue ? 'bg-gradient-to-r from-red-600 to-orange-600' : 'bg-gradient-to-r from-amber-600 to-yellow-600'
        }`}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <Bell className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                Rappel - {rappel.prenom} {rappel.nom}
                {overdue && (
                  <span className="text-xs bg-red-500 px-2 py-0.5 rounded-full">EN RETARD</span>
                )}
              </h2>
              <p className="text-sm text-white/70">
                {formatDate(rappel.date_rappel)} à {rappel.heure_rappel || '--:--'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                title="Modifier"
              >
                <Edit3 className="w-5 h-5 text-white" />
              </button>
            )}
            <button onClick={onClose} className="p-2 text-white/70 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          
          {/* Bouton Appeler */}
          <div className="flex gap-3">
            <button
              onClick={handleClickToCall}
              className="flex-1 flex items-center justify-center gap-3 bg-emerald-500 hover:bg-emerald-600 text-white py-4 rounded-xl font-semibold transition-colors"
              data-testid="ringover-call-button"
            >
              <Phone className="w-6 h-6" />
              <span className="text-xl">{formatPhoneDisplay(rappel.telephone)}</span>
              <span className="text-sm opacity-80">• Appeler</span>
            </button>
            {/* Bouton pour cacher Ringover */}
            <button
              onClick={() => window.ringoverHide && window.ringoverHide()}
              className="px-4 py-3 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded-xl transition-colors"
              title="Masquer Ringover"
            >
              <X className="w-5 h-5" />
            </button>
            <button
              onClick={handleDelete}
              disabled={deleting}
              className="px-4 py-3 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-xl transition-colors disabled:opacity-50"
              title="Supprimer le rappel"
            >
              {deleting ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}
            </button>
          </div>

          {/* Infos prospect */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <User className="w-5 h-5 text-blue-400" />
                Informations Prospect (Lead)
              </h3>
              {/* Info: Ce lead n'est pas encore un client */}
              <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-500/20 text-amber-400 rounded-lg text-sm">
                <Info className="w-4 h-4" />
                Lead en attente de qualification
              </div>
            </div>
            
            {isEditing ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Prénom</label>
                  <input
                    type="text"
                    value={formData.prenom}
                    onChange={e => setFormData({...formData, prenom: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Nom</label>
                  <input
                    type="text"
                    value={formData.nom}
                    onChange={e => setFormData({...formData, nom: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Téléphone</label>
                  <input
                    type="tel"
                    value={formData.telephone}
                    onChange={e => setFormData({...formData, telephone: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-slate-400 mb-1">Adresse</label>
                  <input
                    type="text"
                    value={formData.adresse}
                    onChange={e => setFormData({...formData, adresse: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Code Postal</label>
                  <input
                    type="text"
                    value={formData.code_postal}
                    onChange={e => setFormData({...formData, code_postal: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Ville</label>
                  <input
                    type="text"
                    value={formData.ville}
                    onChange={e => setFormData({...formData, ville: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <div className="flex items-center gap-3">
                  <span className="text-2xl font-bold text-white">{rappel.prenom} {rappel.nom}</span>
                </div>
                {/* Numéro HTML pur pour Ringover */}
                <div className="flex items-center gap-2 text-emerald-400">
                  <Phone className="w-4 h-4" />
                  <span 
                    dangerouslySetInnerHTML={{
                      __html: `<a href="tel:${formatPhoneTel(rappel.telephone)}" data-phone="${formatPhoneTel(rappel.telephone)}" style="color: inherit; text-decoration: none;">${formatPhoneDisplay(rappel.telephone)}</a>`
                    }}
                  />
                </div>
                {rappel.email && (
                  <div className="flex items-center gap-2 text-slate-300">
                    <span className="text-slate-500">@</span>
                    <span>{rappel.email}</span>
                  </div>
                )}
                <div className="flex items-center gap-2 text-slate-300">
                  <MapPin className="w-4 h-4 text-blue-400" />
                  <span>{rappel.code_postal} {rappel.ville}</span>
                  <span className="text-slate-500">•</span>
                  <span className="text-blue-400 font-medium">DPT {rappel.departement}</span>
                </div>
                {rappel.adresse && (
                  <div className="text-slate-400 text-sm pl-6">{rappel.adresse}</div>
                )}
              </div>
            )}
          </div>

          {/* Infos source */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 space-y-3">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <FileText className="w-5 h-5 text-purple-400" />
              Source & Projet
            </h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-slate-500">Source:</span>
                <span className="ml-2 text-white">{rappel.source || 'N/A'}</span>
              </div>
              {rappel.projet_externe_id && (
                <div>
                  <span className="text-slate-500">ID Projet:</span>
                  <span className="ml-2 text-amber-400">{rappel.projet_externe_id}</span>
                </div>
              )}
            </div>
            {rappel.infos_complementaires && (
              <div className="bg-slate-900/50 rounded-lg p-3">
                <div className="text-xs text-slate-500 mb-1 flex items-center gap-1">
                  <Info className="w-3 h-3" />
                  Informations complémentaires
                </div>
                <p className="text-slate-300">{rappel.infos_complementaires}</p>
              </div>
            )}
            {rappel.message && (
              <div className="bg-slate-900/50 rounded-lg p-3">
                <div className="text-xs text-slate-500 mb-1">Message initial</div>
                <p className="text-slate-300">{rappel.message}</p>
              </div>
            )}
          </div>

          {/* Date et heure du rappel */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 space-y-3">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <Calendar className="w-5 h-5 text-amber-400" />
              Planification du Rappel
            </h3>
            
            {isEditing ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Date</label>
                  <input
                    type="date"
                    value={formData.date_rappel}
                    onChange={e => setFormData({...formData, date_rappel: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-400 mb-1">Heure</label>
                  <input
                    type="time"
                    value={formData.heure_rappel}
                    onChange={e => setFormData({...formData, heure_rappel: e.target.value})}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white"
                  />
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <span className="text-white">{formatDate(rappel.date_rappel)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-slate-400" />
                  <span className="text-white text-xl font-mono">{rappel.heure_rappel || '--:--'}</span>
                </div>
              </div>
            )}
          </div>

          {/* Notes / Commentaires */}
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 space-y-3">
            <h3 className="text-white font-semibold flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-teal-400" />
              Notes & Commentaires
            </h3>
            
            {isEditing ? (
              <textarea
                value={formData.notes}
                onChange={e => setFormData({...formData, notes: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-600 rounded-lg text-white min-h-[100px]"
                placeholder="Notes sur ce rappel..."
              />
            ) : (
              <div className="bg-slate-900/50 rounded-lg p-3">
                {rappel.notes ? (
                  <p className="text-slate-300 whitespace-pre-wrap">{rappel.notes}</p>
                ) : (
                  <p className="text-slate-500 italic">Aucune note</p>
                )}
              </div>
            )}
          </div>

          {/* Historique */}
          {rappel.historique && rappel.historique.length > 0 && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 space-y-3">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <Clock className="w-5 h-5 text-slate-400" />
                Historique
              </h3>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {rappel.historique.map((entry, idx) => (
                  <div key={idx} className="text-sm border-l-2 border-slate-600 pl-3 py-1">
                    <div className="text-slate-400 text-xs">
                      {new Date(entry.date).toLocaleString('fr-FR')}
                    </div>
                    <div className="text-slate-300">{entry.details}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        {isEditing && (
          <div className="border-t border-slate-700 p-4 flex justify-end gap-3">
            <button
              onClick={() => setIsEditing(false)}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 text-white rounded-lg"
            >
              Annuler
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-amber-500 hover:bg-amber-400 text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Enregistrer
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default RappelDetailModal;
